/**
 * Code QR (ISO/IEC 18004:2015) -- encodeur pur, zero E/S, zero dependance : de quoi dessiner le code de connexion
 * d'un reseau Wi-Fi (`wifi-qr.ts`) sans faire sortir le mot de passe du poste.
 *
 * Pourquoi ecrit ici et pas importe : une bibliotheque de codes QR dans le coffre verrait passer en clair le mot de
 * passe du reseau ; celle-ci tient en un fichier relu ligne a ligne (`docs/DEPENDENCY-POLICY.md`). Ce n'est PAS de la
 * cryptographie : Reed-Solomon n'est ici qu'un code correcteur d'erreurs, il ne protege rien.
 *
 * Perimetre volontairement borne a ce qu'un code de connexion exige :
 * - mode octet (UTF-8), le seul qui porte un SSID et un mot de passe quelconques ;
 * - niveau de correction M (15 %), celui des codes Wi-Fi d'Android, d'iOS et des box ;
 * - versions 1 a 13, soit 331 octets au plus : le pire cas reel -- SSID de 32 octets et cle de 63 caracteres, tout
 *   a echapper, reseau masque -- fait 215 octets, et ne tiendrait pas en version 10 (213).
 *
 * Verifie contre deux encodeurs de reference (segno 1.6.6, python-qrcode 8.2) module par module, pour chaque
 * version et chaque masque : `test/qr-code.test.ts`.
 */

/** Une ligne par module : `true` = sombre. `modules[ligne][colonne]`. */
export interface QrCode {
  readonly version: number;
  readonly size: number;
  readonly mask: number;
  readonly modules: readonly (readonly boolean[])[];
}

/**
 * Blocs du niveau M, versions 1 a 13 (ISO/IEC 18004:2015, tableau 9) : octets de correction par bloc, puis groupes
 * [nombre de blocs, octets de donnees par bloc].
 */
const M_BLOCKS: readonly { readonly ecPerBlock: number; readonly groups: readonly (readonly [number, number])[] }[] = [
  { ecPerBlock: 10, groups: [[1, 16]] },
  { ecPerBlock: 16, groups: [[1, 28]] },
  { ecPerBlock: 26, groups: [[1, 44]] },
  { ecPerBlock: 18, groups: [[2, 32]] },
  { ecPerBlock: 24, groups: [[2, 43]] },
  { ecPerBlock: 16, groups: [[4, 27]] },
  { ecPerBlock: 18, groups: [[4, 31]] },
  { ecPerBlock: 22, groups: [[2, 38], [2, 39]] },
  { ecPerBlock: 22, groups: [[3, 36], [2, 37]] },
  { ecPerBlock: 26, groups: [[4, 43], [1, 44]] },
  { ecPerBlock: 30, groups: [[1, 50], [4, 51]] },
  { ecPerBlock: 22, groups: [[6, 36], [2, 37]] },
  { ecPerBlock: 22, groups: [[8, 37], [1, 38]] },
];

/** Centres des motifs d'alignement (ISO/IEC 18004:2015, annexe E), versions 1 a 13. */
const ALIGNMENT_CENTERS: readonly (readonly number[])[] = [
  [],
  [6, 18],
  [6, 22],
  [6, 26],
  [6, 30],
  [6, 34],
  [6, 22, 38],
  [6, 24, 42],
  [6, 26, 46],
  [6, 28, 50],
  [6, 30, 54],
  [6, 32, 58],
  [6, 34, 62],
];

export const QR_MAX_VERSION = M_BLOCKS.length;

function dataCodewords(version: number): number {
  return M_BLOCKS[version - 1]!.groups.reduce((sum, [count, size]) => sum + count * size, 0);
}

/** Bits du compteur de longueur en mode octet : 8 jusqu'a la version 9, 16 ensuite. */
function countBits(version: number): number {
  return version <= 9 ? 8 : 16;
}

/** Octets au plus que porte la version (mode octet, niveau M). */
export function qrByteCapacity(version: number): number {
  return Math.floor((dataCodewords(version) * 8 - 4 - countBits(version)) / 8);
}

export const QR_MAX_BYTES = qrByteCapacity(QR_MAX_VERSION);

// --- Corps de Galois GF(256), polynome 0x11D (ISO/IEC 18004, 7.5.2) ---

const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
{
  let x = 1;
  for (let i = 0; i < 255; i++) {
    GF_EXP[i] = x;
    GF_LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255]!;
}

function gfMultiply(a: number, b: number): number {
  return a === 0 || b === 0 ? 0 : GF_EXP[GF_LOG[a]! + GF_LOG[b]!]!;
}

/** Polynome generateur de degre `degree` : produit des (x - α^i), coefficients du plus haut degre au plus bas. */
function reedSolomonGenerator(degree: number): number[] {
  let generator = [1];
  for (let i = 0; i < degree; i++) {
    const next = new Array<number>(generator.length + 1).fill(0);
    for (let j = 0; j < generator.length; j++) {
      next[j]! ^= generator[j]!;
      next[j + 1]! ^= gfMultiply(generator[j]!, GF_EXP[i]!);
    }
    generator = next;
  }
  return generator;
}

/** Octets de correction d'un bloc : reste de la division de `data · x^degree` par le generateur. */
export function reedSolomonRemainder(data: readonly number[], degree: number): number[] {
  const generator = reedSolomonGenerator(degree);
  const remainder = new Array<number>(degree).fill(0);
  for (const byte of data) {
    const factor = byte ^ remainder.shift()!;
    remainder.push(0);
    for (let i = 0; i < degree; i++) remainder[i]! ^= gfMultiply(generator[i + 1]!, factor);
  }
  return remainder;
}

/** Format (niveau M = 00, masque) protege par BCH(15,5) puis masque par 101010000010010. */
export function qrFormatBits(mask: number): number {
  const data = mask & 7;
  let remainder = data;
  for (let i = 0; i < 10; i++) remainder = (remainder << 1) ^ ((remainder >>> 9) * 0x537);
  return ((data << 10) | (remainder & 0x3ff)) ^ 0x5412;
}

/** Information de version (versions 7 et plus), protegee par BCH(18,6). */
export function qrVersionBits(version: number): number {
  let remainder = version;
  for (let i = 0; i < 12; i++) remainder = (remainder << 1) ^ ((remainder >>> 11) * 0x1f25);
  return (version << 12) | (remainder & 0xfff);
}

/** Flux de donnees : mode octet, longueur, octets, terminateur, bourrage 0xEC / 0x11 jusqu'a la capacite. */
function encodeData(bytes: Uint8Array, version: number): number[] {
  const capacityBits = dataCodewords(version) * 8;
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i--) bits.push((value >>> i) & 1);
  };
  push(0b0100, 4);
  push(bytes.length, countBits(version));
  for (const byte of bytes) push(byte, 8);
  push(0, Math.min(4, capacityBits - bits.length));
  push(0, (8 - (bits.length % 8)) % 8);
  const codewords: number[] = [];
  for (let i = 0; i < bits.length; i += 8) codewords.push(bits.slice(i, i + 8).reduce((byte, bit) => (byte << 1) | bit, 0));
  for (let pad = 0xec; codewords.length < capacityBits / 8; pad ^= 0xec ^ 0x11) codewords.push(pad);
  return codewords;
}

/** Decoupe en blocs, correction par bloc, puis entrelacement colonne par colonne (donnees d'abord, correction ensuite). */
function interleave(data: readonly number[], version: number): number[] {
  const { ecPerBlock, groups } = M_BLOCKS[version - 1]!;
  const blocks: number[][] = [];
  let offset = 0;
  for (const [count, size] of groups) {
    for (let i = 0; i < count; i++) {
      blocks.push(data.slice(offset, offset + size));
      offset += size;
    }
  }
  const corrections = blocks.map((block) => reedSolomonRemainder(block, ecPerBlock));
  const result: number[] = [];
  const longest = Math.max(...blocks.map((block) => block.length));
  for (let i = 0; i < longest; i++) for (const block of blocks) if (i < block.length) result.push(block[i]!);
  for (let i = 0; i < ecPerBlock; i++) for (const correction of corrections) result.push(correction[i]!);
  return result;
}

const MASKS: readonly ((row: number, col: number) => boolean)[] = [
  (r, c) => (r + c) % 2 === 0,
  (r) => r % 2 === 0,
  (_r, c) => c % 3 === 0,
  (r, c) => (r + c) % 3 === 0,
  (r, c) => (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0,
  (r, c) => ((r * c) % 2) + ((r * c) % 3) === 0,
  (r, c) => (((r * c) % 2) + ((r * c) % 3)) % 2 === 0,
  (r, c) => (((r + c) % 2) + ((r * c) % 3)) % 2 === 0,
];

class Grid {
  readonly modules: boolean[][];
  /** Modules des motifs fixes (reperes, synchronisation, alignement, format, version) : ni donnees ni masque. */
  readonly reserved: boolean[][];

  constructor(readonly size: number) {
    this.modules = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
    this.reserved = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));
  }

  set(row: number, col: number, dark: boolean): void {
    this.modules[row]![col] = dark;
    this.reserved[row]![col] = true;
  }

  copy(): Grid {
    const grid = new Grid(this.size);
    for (let r = 0; r < this.size; r++) {
      grid.modules[r] = [...this.modules[r]!];
      grid.reserved[r] = [...this.reserved[r]!];
    }
    return grid;
  }
}

function drawFinder(grid: Grid, row: number, col: number): void {
  // Repere 7x7 et son separateur clair : distance de Chebyshev au centre 0-1 sombre, 2 claire, 3 sombre, 4 claire.
  for (let dr = -4; dr <= 4; dr++) {
    for (let dc = -4; dc <= 4; dc++) {
      const r = row + 3 + dr;
      const c = col + 3 + dc;
      if (r < 0 || c < 0 || r >= grid.size || c >= grid.size) continue;
      const distance = Math.max(Math.abs(dr), Math.abs(dc));
      grid.set(r, c, distance !== 2 && distance !== 4);
    }
  }
}

function drawFunctionPatterns(grid: Grid, version: number): void {
  const { size } = grid;
  for (let i = 0; i < size; i++) {
    grid.set(6, i, i % 2 === 0);
    grid.set(i, 6, i % 2 === 0);
  }
  drawFinder(grid, 0, 0);
  drawFinder(grid, 0, size - 7);
  drawFinder(grid, size - 7, 0);
  const centers = ALIGNMENT_CENTERS[version - 1]!;
  const last = centers.length - 1;
  for (let i = 0; i < centers.length; i++) {
    for (let j = 0; j < centers.length; j++) {
      // Les trois coins occupes par un repere n'ont pas de motif d'alignement.
      if ((i === 0 && j === 0) || (i === 0 && j === last) || (i === last && j === 0)) continue;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) grid.set(centers[i]! + dr, centers[j]! + dc, Math.max(Math.abs(dr), Math.abs(dc)) !== 1);
      }
    }
  }
  // Zones du format reservees des maintenant (ecrites par `drawFormat`), module sombre fixe compris.
  drawFormat(grid, 0);
  if (version >= 7) {
    const bits = qrVersionBits(version);
    for (let i = 0; i < 18; i++) {
      const dark = ((bits >>> i) & 1) === 1;
      const a = size - 11 + (i % 3);
      const b = Math.floor(i / 3);
      grid.set(b, a, dark);
      grid.set(a, b, dark);
    }
  }
}

/** Les deux copies du format, et le module sombre fixe (ligne 4V+9, colonne 8). */
function drawFormat(grid: Grid, mask: number): void {
  const { size } = grid;
  const bits = qrFormatBits(mask);
  const bit = (i: number): boolean => ((bits >>> i) & 1) === 1;
  for (let i = 0; i <= 5; i++) grid.set(i, 8, bit(i));
  grid.set(7, 8, bit(6));
  grid.set(8, 8, bit(7));
  grid.set(8, 7, bit(8));
  for (let i = 9; i < 15; i++) grid.set(8, 14 - i, bit(i));
  for (let i = 0; i < 8; i++) grid.set(8, size - 1 - i, bit(i));
  for (let i = 8; i < 15; i++) grid.set(size - 15 + i, 8, bit(i));
  grid.set(size - 8, 8, true);
}

/** Placement en zigzag, par paires de colonnes depuis le coin bas-droit, la colonne 6 (synchronisation) sautee. */
function drawCodewords(grid: Grid, codewords: readonly number[]): void {
  const { size } = grid;
  const total = codewords.length * 8;
  let index = 0;
  for (let right = size - 1; right >= 1; right -= 2) {
    if (right === 6) right = 5;
    const upward = ((right + 1) & 2) === 0;
    for (let step = 0; step < size; step++) {
      const row = upward ? size - 1 - step : step;
      for (let j = 0; j < 2; j++) {
        const col = right - j;
        if (grid.reserved[row]![col]) continue;
        // Au-dela des octets : bits de reste, clairs (versions 2 a 6).
        if (index < total) grid.modules[row]![col] = ((codewords[index >>> 3]! >>> (7 - (index & 7))) & 1) === 1;
        index++;
      }
    }
  }
}

function applyMask(grid: Grid, mask: number): void {
  const test = MASKS[mask]!;
  for (let r = 0; r < grid.size; r++) {
    for (let c = 0; c < grid.size; c++) if (!grid.reserved[r]![c] && test(r, c)) grid.modules[r]![c] = !grid.modules[r]![c];
  }
}

const FINDER_LIKE = [
  [true, false, true, true, true, false, true, false, false, false, false],
  [false, false, false, false, true, false, true, true, true, false, true],
];

/** Penalite d'un masque (ISO/IEC 18004:2015, 7.8.3) : series, carres 2x2, faux reperes, desequilibre sombre/clair. */
function penalty(modules: readonly (readonly boolean[])[]): number {
  const size = modules.length;
  const at = (r: number, c: number, transposed: boolean): boolean => (transposed ? modules[c]![r]! : modules[r]![c]!);
  let score = 0;
  for (const transposed of [false, true]) {
    for (let r = 0; r < size; r++) {
      let run = 1;
      for (let c = 1; c <= size; c++) {
        if (c < size && at(r, c, transposed) === at(r, c - 1, transposed)) {
          run++;
          continue;
        }
        if (run >= 5) score += 3 + (run - 5);
        run = 1;
      }
      for (let c = 0; c + 11 <= size; c++) {
        for (const pattern of FINDER_LIKE) if (pattern.every((dark, k) => at(r, c + k, transposed) === dark)) score += 40;
      }
    }
  }
  for (let r = 0; r + 1 < size; r++) {
    for (let c = 0; c + 1 < size; c++) {
      const dark = modules[r]![c];
      if (modules[r]![c + 1] === dark && modules[r + 1]![c] === dark && modules[r + 1]![c + 1] === dark) score += 3;
    }
  }
  const darkCount = modules.reduce((sum, row) => sum + row.filter(Boolean).length, 0);
  const total = size * size;
  score += Math.floor(Math.abs(darkCount * 20 - total * 10) / total) * 10;
  return score;
}

/**
 * Code QR du texte (UTF-8, mode octet, niveau M), dans la plus petite version qui le porte ; `null` au-dela de
 * `QR_MAX_BYTES` octets. Le masque est celui de moindre penalite, sauf s'il est impose (vecteurs de test).
 */
export function encodeQr(text: string, options: { readonly mask?: number; readonly version?: number } = {}): QrCode | null {
  const bytes = new TextEncoder().encode(text);
  let version = options.version ?? 1;
  while (version <= QR_MAX_VERSION && bytes.length > qrByteCapacity(version)) {
    if (options.version !== undefined) return null;
    version++;
  }
  if (version < 1 || version > QR_MAX_VERSION) return null;
  const base = new Grid(version * 4 + 17);
  drawFunctionPatterns(base, version);
  drawCodewords(base, interleave(encodeData(bytes, version), version));

  let best: { grid: Grid; mask: number; score: number } | null = null;
  const candidates = options.mask === undefined ? [0, 1, 2, 3, 4, 5, 6, 7] : [options.mask & 7];
  for (const mask of candidates) {
    const grid = base.copy();
    applyMask(grid, mask);
    drawFormat(grid, mask);
    const score = candidates.length === 1 ? 0 : penalty(grid.modules);
    if (best === null || score < best.score) best = { grid, mask, score };
  }
  const { grid, mask } = best!;
  return { version, size: grid.size, mask, modules: grid.modules };
}

/**
 * Trace SVG des modules sombres (un chemin, un carre par module), dans un repere de `size + 2 * quiet` unites :
 * la zone de silence de 4 modules qu'exige la norme est comprise. Le chemin ne porte que de la geometrie.
 */
export function qrSvgPath(code: QrCode, quiet = 4): string {
  const parts: string[] = [];
  for (let r = 0; r < code.size; r++) {
    for (let c = 0; c < code.size; c++) if (code.modules[r]![c]) parts.push(`M${c + quiet} ${r + quiet}h1v1h-1z`);
  }
  return parts.join('');
}
